// Funil — relatório AARRR (aquisição → ativação → receita → retenção) com quebras por
// canal, dispositivo, cohort semanal, funil por caso, adoção de features, caminhos e
// insights gerados no servidor a partir dos números. Fonte: GET /funil (só leitura).
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, Badge } from '@tremor/react';
import { Filter } from 'lucide-react';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';
import { formatRangeShort, useDateRange, useDateRangeKey } from '@/lib/dateRange';

// ----------------------------------------------------------------------------- tipos
interface Step { key: string; label: string; count: number; rate_from_previous: number | null; rate_from_top: number | null }
interface Device { device: string; visitantes: number; cta: number; signups: number; uploaders: number; compradores: number; conv_signup: number | null; conv_upload: number | null }
interface Bucket { label: string; n: number }
interface Canal { canal: string; visitantes: number; signups: number; uploaders: number; completaram: number; pagaram: number; creditos: number; conv_visita_signup: number | null; conv_signup_upload: number | null; conv_signup_pago: number | null }
interface Cohort { semana: string; tamanho: number; cells: { k: number; n: number; rate: number | null; madura: boolean }[] }
interface Feature { nome: string; base: number; usuarios: number; eventos: number; adocao: number | null }
interface PathRow { path: string; n: number }
interface Insight { severidade: 'alta' | 'média' | 'info' | 'baixa'; area: string; texto: string }
interface FunilData {
  window: { start: string; end: string; days: number };
  as_of: string;
  // [ASSISTIDO 22/10] `assistidos` = contas que NÓS abrimos e o cliente
  // ainda não acessou. Ficam FORA dos passos (não são aquisição) e
  // aparecem como sub-rótulo discreto sob o funil.
  landing: { steps: Step[]; devices: Device[]; assistidos: number };
  ativacao: { steps: Step[]; assistidos: number; ttv: { med_h_signup_upload: number | null; med_h_completo_download: number | null; med_h_signup_compra: number | null; buckets_signup_upload: Bucket[]; repetiram_upload: number; tres_calculos: number } };
  casos: { steps: Step[]; outcomes: Record<string, number>; primeiro_caso: { erro: { n: number; voltou: number | null; pagou: number | null }; ok: { n: number; voltou: number | null; pagou: number | null } } };
  monetizacao: { steps: Step[]; med_h_bloqueio_compra: number | null; compras: number; orgs_compraram: number; orgs_recorrentes: number; compras_de_recorrentes: number; creditos_vendidos: number; receita_brl: number; receita_n: number; pacotes: { pacote: string; n: number; creditos: number }[] };
  retencao: { cohorts: Cohort[] };
  canais: Canal[];
  caminhos: { entrada: PathRow[]; saida: PathRow[]; transicao: PathRow[]; sessoes: number; bounce: number | null; med_min: number | null };
  features: Feature[];
  heatmap: { grid: number[][]; dias: string[] };
  concentracao: { top10: number; total: number; uploaders: number };
  insights: Insight[];
}

// ----------------------------------------------------------------------------- utils
const fmtInt = (n: number | null | undefined) => (n == null ? '—' : new Intl.NumberFormat('pt-BR').format(n));
const fmtPct = (x: number | null | undefined, d = 0) => (x == null ? '—' : `${(x * 100).toFixed(d)}%`);
const fmtH = (h: number | null | undefined) => (h == null ? '—' : h < 1 ? `${Math.round(h * 60)} min` : h < 48 ? `${h.toFixed(1)} h` : `${(h / 24).toFixed(1)} d`);
const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const FEATURE_LABEL: Record<string, string> = {
  review_opened: 'Revisão aberta', ficha_opened: 'Ficha aberta', edit_saved: 'Edição salva', recalc_clicked: 'Recalcular',
  cartao_opened: 'Cartão de ponto', acordo_sim_opened: 'Simular acordo', concierge_opened: 'Concierge', memoria_download: 'Memória baixada',
  chat_message_sent: 'Chat', feedback_thumb: '👍/👎',
};

// ----------------------------------------------------------------------------- componentes
function Funnel({ steps, accent = 'bg-emerald-500' }: { steps: Step[]; accent?: string }) {
  const top = steps[0]?.count || 1;
  return (
    <div className="space-y-2">
      {steps.map((s, i) => (
        <div key={s.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline justify-between gap-2 text-[13px]">
              <span className="truncate font-medium text-gray-900 dark:text-gray-50">{s.label}</span>
              <span className="tabular-nums font-bold text-gray-900 dark:text-gray-50">{fmtInt(s.count)}</span>
            </div>
            <div className="mt-1 h-2.5 w-full rounded bg-gray-100 dark:bg-gray-700">
              <div className={`h-2.5 rounded ${accent}`} style={{ width: `${Math.max(1, Math.round((s.count / top) * 100))}%` }} />
            </div>
          </div>
          <div className="w-[104px] text-right text-[12px] leading-tight tabular-nums text-gray-700 dark:text-gray-200">
            {i === 0 ? <span className="font-semibold text-gray-700 dark:text-gray-200">100%</span> : (
              <>
                <div><b className="text-[13px] text-gray-900 dark:text-gray-50">{fmtPct(s.rate_from_previous)}</b> do anterior</div>
                <div className="font-medium">{fmtPct(s.rate_from_top)} do topo</div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2">
      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">{label}</div>
      <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-50">{value}</div>
      {hint && <div className="text-[12px] text-gray-700 dark:text-gray-200">{hint}</div>}
    </div>
  );
}

function SimpleTable({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead><tr className="border-b border-gray-300 dark:border-gray-600 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">{head.map((h, i) => <th key={h} className={`py-1.5 pr-3 ${i > 0 ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
              {r.map((c, j) => <td key={j} className={`py-1.5 pr-3 tabular-nums ${j > 0 ? 'text-right text-gray-900 dark:text-gray-50' : 'font-semibold text-gray-900 dark:text-gray-50'}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SEV: Record<Insight['severidade'], string> = { alta: 'red', média: 'amber', info: 'blue', baixa: 'gray' };

function heatColor(v: number, max: number) {
  if (!v || !max) return 'rgba(5,150,105,0.12)';
  const a = 0.3 + 0.7 * Math.min(1, v / max);
  return `rgba(5,150,105,${a.toFixed(2)})`;
}

// ----------------------------------------------------------------------------- página
export function FunilPage() {
  const { range, apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();
  const q = useQuery<FunilData>({
    queryKey: ['funil', rangeKey],
    queryFn: async () => (await api().get('/funil', { params: apiParams })).data,
    staleTime: 5 * 60_000,
  });

  if (q.isLoading) return <LoadingSkeleton />;
  if (q.isError || !q.data) return <Card><Text className="text-red-600 dark:text-red-400">Erro ao carregar o funil: {extractErrorMessage(q.error, 'falha na consulta')}</Text></Card>;
  const d = q.data;
  const maxHeat = Math.max(1, ...d.heatmap.grid.flat());
  const alta = d.insights.filter(i => i.severidade === 'alta');
  const resto = d.insights.filter(i => i.severidade !== 'alta');
  const md = d.landing.devices.find(x => x.device === 'mobile');
  const dd = d.landing.devices.find(x => x.device === 'desktop');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <Title>Funil</Title>
          <Text>Aquisição → ativação → receita → retenção, com canais, dispositivos, cohorts e caminhos. Janela: {formatRangeShort(range)} ({d.window.days} dias). Orgs internas excluídas.</Text>
        </div>
        <Badge color="gray" icon={Filter}>{d.insights.length} insights</Badge>
      </div>

      {/* Insights */}
      <Card>
        <Title className="text-base">Insights do período</Title>
        <Text className="mb-3">Gerados a partir dos números abaixo. Vermelho = pede ação; azul = leitura.</Text>
        <div className="grid gap-2 md:grid-cols-2">
          {[...alta, ...resto].map((i, k) => (
            <div key={k} className={`flex gap-2 rounded-lg border px-3 py-2 text-[13px] leading-snug ${i.severidade === 'alta' ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950' : i.severidade === 'média' ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'}`}>
              <Badge size="xs" color={SEV[i.severidade]}>{i.area}</Badge>
              <span className="text-gray-900 dark:text-gray-50">{i.texto}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* AARRR em 4 funis */}
      <Grid numItemsMd={2} className="gap-4">
        <Card>
          <Title className="text-base">1 · Aquisição — landing → conta</Title>
          <Text className="mb-3">Visitantes anônimos do site (source=website). Conta e login vêm da tabela de usuários.</Text>
          <Funnel steps={d.landing.steps} accent="bg-sky-500" />
          {d.landing.assistidos > 0 && (
            <Text className="mt-2 text-[11px] text-amber-600">
              + {d.landing.assistidos} cadastro(s) assistido(s) fora da conta — conta aberta por nós para o prospect; só vira usuário adquirido no 1º login dele.
            </Text>
          )}
          {md && dd && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Kpi label="Mobile → conta" value={fmtPct(md.conv_signup, 1)} hint={`${fmtInt(md.visitantes)} visitantes · ${fmtPct(md.conv_upload)} dos signups enviam`} />
              <Kpi label="Desktop → conta" value={fmtPct(dd.conv_signup, 1)} hint={`${fmtInt(dd.visitantes)} visitantes · ${fmtPct(dd.conv_upload)} dos signups enviam`} />
            </div>
          )}
        </Card>
        <Card>
          <Title className="text-base">2 · Ativação — cohort de quem se cadastrou na janela</Title>
          <Text className="mb-3">Cada passo conta usuários únicos criados no período. Pagamento = compra da org após o cadastro.</Text>
          <Funnel steps={d.ativacao.steps} />
          {d.ativacao.assistidos > 0 && (
            <Text className="mt-2 text-[11px] text-amber-600">
              + {d.ativacao.assistidos} cadastro(s) assistido(s) fora do cohort (ainda não acessaram sozinhos).
            </Text>
          )}
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Kpi label="Cadastro → 1º envio" value={fmtH(d.ativacao.ttv.med_h_signup_upload)} hint="mediana" />
            <Kpi label="Concluído → download" value={fmtH(d.ativacao.ttv.med_h_completo_download)} hint="mediana" />
            <Kpi label="Cadastro → 1ª compra" value={fmtH(d.ativacao.ttv.med_h_signup_compra)} hint="mediana" />
          </div>
          <div className="mt-3">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">Quando acontece o 1º envio</div>
            <div className="flex h-3 w-full overflow-hidden rounded bg-gray-100 dark:bg-gray-700">
              {d.ativacao.ttv.buckets_signup_upload.map((b, i) => {
                const tot = d.ativacao.ttv.buckets_signup_upload.reduce((a, x) => a + x.n, 0) || 1;
                const cols = ['bg-emerald-600', 'bg-emerald-400', 'bg-amber-400', 'bg-orange-400', 'bg-gray-300'];
                return <div key={b.label} title={`${b.label}: ${b.n}`} className={cols[i]} style={{ width: `${(b.n / tot) * 100}%` }} />;
              })}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 text-[12px] text-gray-800 dark:text-gray-100">
              {d.ativacao.ttv.buckets_signup_upload.map(b => <span key={b.label}>{b.label}: <b>{fmtInt(b.n)}</b></span>)}
              <span className="ml-auto">2+ envios: <b>{fmtInt(d.ativacao.ttv.repetiram_upload)}</b> · 3+ concluídos: <b>{fmtInt(d.ativacao.ttv.tres_calculos)}</b></span>
            </div>
          </div>
        </Card>
        <Card>
          <Title className="text-base">3 · Casos — do envio ao recálculo</Title>
          <Text className="mb-3">Processos criados na janela (sem lote). Eventos de download/revisão casados por processo_id.</Text>
          <Funnel steps={d.casos.steps} accent="bg-violet-500" />
          <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5">
            <Kpi label="Erro" value={fmtInt(d.casos.outcomes.erro)} hint={fmtPct(d.casos.outcomes.erro / Math.max(1, d.casos.steps[0].count))} />
            <Kpi label="Sem crédito" value={fmtInt(d.casos.outcomes.bloqueados)} />
            <Kpi label=".pjc baixado" value={fmtInt(d.casos.outcomes.pjc)} hint={`${fmtPct(d.casos.outcomes.pjc / Math.max(1, d.casos.outcomes.concluidos))} dos concluídos`} />
            <Kpi label="Memória baixada" value={fmtInt(d.casos.outcomes.memoria)} hint={`${fmtPct(d.casos.outcomes.memoria / Math.max(1, d.casos.outcomes.concluidos))} dos concluídos`} />
            <Kpi label="👍 / 👎" value={`${d.casos.outcomes.thumb_up} / ${d.casos.outcomes.thumb_down}`} hint="por caso" />
          </div>
          <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">Primeiro caso do usuário: o que acontece depois</div>
            <SimpleTable head={['1º caso', 'n', 'voltou a enviar', 'comprou']} rows={[
              ['Concluído', d.casos.primeiro_caso.ok.n, fmtPct(d.casos.primeiro_caso.ok.voltou), fmtPct(d.casos.primeiro_caso.ok.pagou)],
              ['Erro', d.casos.primeiro_caso.erro.n, fmtPct(d.casos.primeiro_caso.erro.voltou), fmtPct(d.casos.primeiro_caso.erro.pagou)],
            ]} />
          </div>
        </Card>
        <Card>
          <Title className="text-base">4 · Receita — muro de crédito → compra</Title>
          <Text className="mb-3">Orgs que tiveram um caso bloqueado por falta de crédito na janela.</Text>
          <Funnel steps={d.monetizacao.steps} accent="bg-amber-500" />
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Kpi label="Compras" value={fmtInt(d.monetizacao.compras)} hint={`${fmtInt(d.monetizacao.orgs_compraram)} orgs`} />
            <Kpi label="Receita (Stripe)" value={fmtBRL(d.monetizacao.receita_brl)} hint={`${d.monetizacao.receita_n} eventos purchase`} />
            <Kpi label="Recompra" value={fmtPct(d.monetizacao.orgs_compraram ? d.monetizacao.orgs_recorrentes / d.monetizacao.orgs_compraram : null)} hint="orgs com 2+ compras na vida" />
            <Kpi label="Bloqueio → compra" value={fmtH(d.monetizacao.med_h_bloqueio_compra)} hint="mediana" />
          </div>
          {d.monetizacao.pacotes.length > 0 && (
            <div className="mt-3"><SimpleTable head={['Pacote', 'compras', 'créditos']} rows={d.monetizacao.pacotes.map(p => [p.pacote, p.n, p.creditos])} /></div>
          )}
        </Card>
      </Grid>

      {/* Retenção + canais */}
      <Grid numItemsMd={2} className="gap-4">
        <Card>
          <Title className="text-base">5 · Retenção — cohorts semanais</Title>
          <Text className="mb-3">% da cohort (semana de cadastro) que voltou ao app N semanas depois. Cinza = semana ainda não fechou.</Text>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200"><th className="py-1 text-left">Cohort</th><th className="py-1 text-right pr-2">n</th>{[1, 2, 3, 4, 5, 6].map(k => <th key={k} className="py-1 text-center">S{k}</th>)}</tr></thead>
              <tbody>
                {d.retencao.cohorts.map(c => (
                  <tr key={c.semana}>
                    <td className="py-0.5 font-semibold text-gray-900 dark:text-gray-50">{c.semana}</td>
                    <td className="py-0.5 pr-2 text-right tabular-nums text-gray-900 dark:text-gray-50">{c.tamanho}</td>
                    {c.cells.map(cell => (
                      <td key={cell.k} className="p-0.5">
                        <div className={`rounded px-1 py-1 text-center font-semibold tabular-nums ${cell.madura ? 'text-gray-900 dark:text-gray-50' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`} style={cell.madura ? { background: heatColor(cell.rate || 0, 0.4) } : undefined}>
                          {cell.madura ? fmtPct(cell.rate) : '·'}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Text className="mt-2 text-[12px] text-gray-800 dark:text-gray-100">Uso = qualquer evento logado no app ou processo criado. Concentração: os 10% mais ativos fizeram {fmtPct(d.concentracao.total ? d.concentracao.top10 / d.concentracao.total : null)} dos {fmtInt(d.concentracao.total)} envios.</Text>
        </Card>
        <Card>
          <Title className="text-base">6 · Canais</Title>
          <Text className="mb-3">utm_source do visitante (landing) e do cadastro. Receita em créditos comprados pelas orgs do canal.</Text>
          <SimpleTable head={['Canal', 'visitas', 'contas', 'visita→conta', 'conta→envio', 'concluíram', 'pagaram', 'conta→pago', 'créditos']}
            rows={d.canais.map(c => [c.canal, fmtInt(c.visitantes), fmtInt(c.signups), fmtPct(c.conv_visita_signup, 1), fmtPct(c.conv_signup_upload), fmtInt(c.completaram), fmtInt(c.pagaram), fmtPct(c.conv_signup_pago, 1), fmtInt(c.creditos)])} />
        </Card>
      </Grid>

      {/* Features + caminhos + heatmap */}
      <Grid numItemsMd={3} className="gap-4">
        <Card>
          <Title className="text-base">7 · Adoção de features</Title>
          <Text className="mb-3">Entre usuários com ≥1 cálculo concluído na janela (base {fmtInt(d.features[0]?.base)}).</Text>
          <div className="space-y-1.5">
            {d.features.map(f => (
              <div key={f.nome} className="text-[13px]">
                <div className="flex justify-between"><span className="font-medium text-gray-900 dark:text-gray-50">{FEATURE_LABEL[f.nome] || f.nome}</span><span className="tabular-nums text-gray-900 dark:text-gray-50"><b>{fmtPct(f.adocao)}</b> <span className="text-gray-700 dark:text-gray-200">({f.usuarios})</span></span></div>
                <div className="mt-0.5 h-1.5 rounded bg-gray-100 dark:bg-gray-700"><div className="h-1.5 rounded bg-emerald-500" style={{ width: `${Math.round((f.adocao || 0) * 100)}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <Title className="text-base">8 · Caminhos no app</Title>
          <Text className="mb-3">{fmtInt(d.caminhos.sessoes)} sessões · {fmtPct(d.caminhos.bounce)} com 1 página · mediana {d.caminhos.med_min == null ? '—' : `${d.caminhos.med_min.toFixed(1)} min`}.</Text>
          <div className="grid gap-3 text-[13px]">
            {(['entrada', 'transicao', 'saida'] as const).map(t => (
              <div key={t}>
                <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">{t === 'entrada' ? 'Página de entrada' : t === 'saida' ? 'Página de saída' : 'Transições mais comuns'}</div>
                {d.caminhos[t].slice(0, 6).map(r => (
                  <div key={r.path} className="flex justify-between border-b border-gray-100 dark:border-gray-800 py-0.5"><span className="truncate font-mono text-[12px] text-gray-900 dark:text-gray-50">{r.path}</span><span className="tabular-nums font-semibold text-gray-900 dark:text-gray-50">{fmtInt(r.n)}</span></div>
                ))}
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <Title className="text-base">9 · Quando enviam (BRT)</Title>
          <Text className="mb-3">Processos enviados por dia da semana × hora.</Text>
          <div className="overflow-x-auto">
            <div className="grid gap-[2px]" style={{ gridTemplateColumns: `32px repeat(24, minmax(9px, 1fr))` }}>
              <div />
              {Array.from({ length: 24 }, (_, h) => <div key={h} className="text-center text-[10px] font-medium text-gray-700 dark:text-gray-200">{h % 3 === 0 ? h : ''}</div>)}
              {d.heatmap.grid.map((row, di) => (
                <>
                  <div key={`d${di}`} className="text-[11px] font-semibold text-gray-800 dark:text-gray-100">{d.heatmap.dias[di]}</div>
                  {row.map((v, h) => <div key={`${di}-${h}`} title={`${d.heatmap.dias[di]} ${h}h: ${v}`} className="h-4 rounded-[2px]" style={{ background: heatColor(v, maxHeat) }} />)}
                </>
              ))}
            </div>
          </div>
        </Card>
      </Grid>

      <Text className="text-[12px] text-gray-600 dark:text-gray-300">Atualizado {new Date(d.as_of).toLocaleString('pt-BR')} · consulta ao vivo, sem cache no servidor · leitura apenas.</Text>
    </div>
  );
}
